import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Stepper } from 'components/ui/common/Stepper/Stepper.js';
import { FieldAsterisk } from 'components/ui/form/FormLabel/index.js';
import {
  DialogSmallText,
  FullscreenModal,
  StyledDialogTitle,
} from 'components/ui/modals/FullscreenModal/index.js';
import { CheckBoxText } from 'features/units/components/modals/WarningModal.styles.js';
import type { InputHTMLAttributes } from 'react';

import { Account } from './Account.js';
import { IssueForm } from './IssueForm/IssueForm.js';
import { useIssueModalEffects } from './IssueModal.effects.js';
import { InfoText } from './IssueModal.styled.js';
import type { IssueModalProps } from './IssueModal.types.js';
import { Results } from './Results.js';

export const IssueModal = ({
  onSubmit,
  onClose,
  initialIssueMetadata,
  selectedIssuanceAccount,
  isSubmitting,
  mode,
}: IssueModalProps) => {
  const { checked, setChecked, step, setStep, setIssueData, issueData, initialIssueData } = useIssueModalEffects({
    accountId: selectedIssuanceAccount?.id,
    initialIssueMetadata,
    mode,
  });

  return (
    <FullscreenModal
      open
      onClose={onClose}
      dialogContentProps={{
        sx: {
          pb: 20,
          overflowY: 'scroll',
          display: 'flex',
        },
      }}
    >
      {checked
        ? (
            <Container maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <StyledDialogTitle>Issue SAFcA</StyledDialogTitle>
              <DialogSmallText>
                <FieldAsterisk>*</FieldAsterisk>
                {' '}
                indicates required fields.
              </DialogSmallText>
              {selectedIssuanceAccount && (
                <Account
                  name={selectedIssuanceAccount.name}
                  type={selectedIssuanceAccount.type}
                />
              )}
              <Stepper steps={['Questions', 'Results']} activeStep={step} />
              {step === 0 && (
                <IssueForm
                  mode={mode}
                  initialIssueMetadata={issueData ?? initialIssueData}
                  onSubmit={(data) => {
                    setIssueData(data);
                    setStep(1);
                  }}
                />
              )}
              {step === 1 && issueData && (
                <Results
                  data={issueData}
                  onBack={() => setStep(0)}
                  onSubmit={({ adjustedLCAChoice }) => onSubmit({ ...issueData, adjustedLCAChoice })}
                  isSubmitting={isSubmitting}
                />
              )}
            </Container>
          )
        : (
            <Container
              maxWidth="md"
              style={{ display: 'flex', alignItems: 'center', margin: 'auto' }}
            >
              <Box display="flex" alignItems="center" flexDirection="column">
                <Box sx={{ mb: 2 }}>
                  <Typography>
                    The following pages will lead you through the SAFc issuance process and require you to upload information in order to establish the unit&apos;s:
                  </Typography>
                  <ul>
                    <li>
                      <InfoText>
                        Sustainability Tier (i.e. a classification of the SAFc as SABA Advanced, SABA Eligible, or SCS Eligible),
                      </InfoText>
                    </li>
                    <li>
                      <InfoText>
                        Usability Tier (i.e. a classification of the SAFc as eligible for compliance or voluntary purposes), and
                      </InfoText>
                    </li>
                    <li>
                      <InfoText>
                        Adjusted LCA (i.e. the recalculated lifecycle assessment value consistent with sustainability tier)
                      </InfoText>
                    </li>
                  </ul>
                  <br />
                  <Typography>
                    Most of the required information is the same as in the Proof of Sustainability (POS) or Proof of Compliance (POC) document for the associated batch of SAF.
                  </Typography>
                  <br />
                  <Typography>
                    A detailed explanation of the issuance process is available in the
                    {' '}
                    <a href="https://energy-web-foundation.gitbook.io/safc-registry/user-guide" target="_blank" rel="noopener noreferrer">SAFc Registry User Guide</a>
                    {' '}
                    and
                    {' '}
                    <a href="https://energy-web-foundation.gitbook.io/safc-registry/rulebook" target="_blank" rel="noopener noreferrer">SAFc Registry Rulebook</a>
                    .
                  </Typography>
                </Box>
                <Box display="flex" mt={5} alignItems="center">
                  <Checkbox
                    onChange={e => setChecked(e.target.checked)}
                    checked={checked}
                    id="term_checkbox"
                    slotProps={{
                      input: {
                        'data-testid': 'issue_term_checkbox',
                      } as InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                  <CheckBoxText htmlFor="term_checkbox">
                    I warrant that if the requested information is available in the POS or POC, my entries will be identical to the information provided in the POS or POC.
                  </CheckBoxText>
                </Box>
              </Box>
            </Container>
          )}
    </FullscreenModal>
  );
};
